import { useMemo, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import CodeMirror from '@uiw/react-codemirror'
import { githubDark, githubLight } from '@uiw/codemirror-theme-github'
import { Loader2, File, AlertCircle, Download } from 'lucide-react'
import { useFileContent } from '@/hooks/useProjectCode'
import { getLanguageExtension, getDownloadUrl, formatFileSize, isImageFile } from '@/lib/code-api'
import { Button } from '@/components/ui/button'

interface CodeViewerProps {
  projectId: number
  filePath: string | null
}

export function CodeViewer({ projectId, filePath }: CodeViewerProps) {
  const { t } = useTranslation('conversations')
  const { data, isLoading, error } = useFileContent(projectId, filePath)

  // Detect dark mode
  const [isDark, setIsDark] = useState(() =>
    document.documentElement.classList.contains('dark')
  )

  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains('dark'))
    })
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
    return () => observer.disconnect()
  }, [])

  const extensions = useMemo(() => {
    if (!filePath) return []
    const langExt = getLanguageExtension(filePath)
    return langExt ? [langExt] : []
  }, [filePath])

  // No file selected - full height placeholder
  if (!filePath) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-8 text-center text-muted-foreground bg-muted/20">
        <File className="h-12 w-12 mb-4 opacity-30" />
        <p className="text-sm">{t('code.selectFile')}</p>
      </div>
    )
  }

  // Main layout - always render this structure when file is selected
  return (
    <div className="flex flex-col h-full">
      {/* Header - fixed height, never shrinks */}
      <div className="flex items-center px-3 h-10 border-b bg-muted/30 flex-shrink-0">
        <span className="text-sm font-mono text-muted-foreground truncate">
          {filePath}
        </span>
      </div>

      {/* Editor container - flex-1 to fill space, min-h-0 for scroll, overflow-hidden for boundary */}
      <div className="flex-1 min-h-0 overflow-hidden">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
            <Loader2 className="h-6 w-6 animate-spin mb-2" />
            <span className="text-sm">{t('code.loadingFile')}</span>
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-full p-8 text-center">
            <AlertCircle className="h-8 w-8 text-destructive mb-3" />
            <p className="text-sm text-destructive mb-1">{t('code.fileLoadError')}</p>
            <p className="text-xs text-muted-foreground">
              {error instanceof Error ? error.message : t('code.unknownError')}
            </p>
          </div>
        ) : data?.binary ? (
          // Binary file - show download placeholder
          <div className="flex flex-col items-center justify-center h-full p-8 text-center">
            <File className="h-16 w-16 text-muted-foreground/50 mb-4" />
            <p className="text-sm text-muted-foreground mb-1">{t('code.previewNotAvailable')}</p>
            <p className="text-xs text-muted-foreground mb-4">
              {filePath?.split('/').pop()} ({formatFileSize(data.size)})
            </p>
            <Button
              variant="outline"
              size="sm"
              asChild
            >
              <a href={getDownloadUrl(projectId, filePath!)} download>
                <Download className="h-4 w-4 mr-2" />
                {t('code.downloadFile')}
              </a>
            </Button>
          </div>
        ) : data?.encoding === 'base64' && isImageFile(filePath || '') ? (
          // Image preview
          <div className="flex flex-col items-center h-full p-4 overflow-auto">
            <div className="flex-1 flex items-center justify-center">
              <img
                src={`data:${data.mime_type};base64,${data.content}`}
                alt={filePath?.split('/').pop() || 'Image preview'}
                className="max-w-full max-h-full object-contain rounded border"
              />
            </div>
            <div className="flex-shrink-0 mt-4">
              <Button
                variant="outline"
                size="sm"
                asChild
              >
                <a href={getDownloadUrl(projectId, filePath!)} download>
                  <Download className="h-4 w-4 mr-2" />
                  {t('code.downloadFile')}
                </a>
              </Button>
            </div>
          </div>
        ) : (
          <CodeMirror
            value={data?.content || ''}
            theme={isDark ? githubDark : githubLight}
            extensions={extensions}
            editable={false}
            readOnly={true}
            basicSetup={{
              lineNumbers: true,
              foldGutter: true,
              highlightActiveLineGutter: false,
              highlightActiveLine: false,
            }}
            height="100%"
            className="h-full"
          />
        )}
      </div>
    </div>
  )
}
